import jsPDF from "jspdf";
import { addNeverwinter } from "../fonts/Neverwinter-normal";
import { addBelmont } from "../fonts/Belmont-Normal";
import { addLinotteBold } from "../fonts/linotte-semibold";
import { addLinotte } from "../fonts/linotte-normal";
import { addNewRocker } from "../fonts/NewRocker-Regular";
import { addLinotteSuperBold } from "../fonts/Linotte Bold-normal";
import { addLinotteHeavy } from "../fonts/Linotte Heavy-normal";
import { addGladeano } from "../fonts/Galdeano-Regular-normal";

// Width of full document is about 560
// Height of full document is 850
const documentWidth = 560;
const documentHeight = 850;
const imageWidth = 480;
const imageHeight = 270;

export function initializePdf(): jsPDF {
  const doc = new jsPDF("portrait", "pt", "a4");
  addNeverwinter(doc);
  addBelmont(doc);
  addLinotte(doc);
  addLinotteBold(doc);
  addNewRocker(doc);
  addLinotteSuperBold(doc);
  addLinotteHeavy(doc);
  addGladeano(doc);
  return doc;
}

export const TEXT_SETTINGS = {
  Standard: {
    lineHeightFunction: (lines: number) => lines * 25,
    setDoc: (doc: jsPDF) => {
      doc.setFont("linotte");
      doc.setFontSize(16);
      doc.setTextColor("black");
    },
    maxCharsPerLine: 68,
    width: 10,
    characterWidth: 7,
    textOptions: {},
  },
  Center: {
    lineHeightFunction: (lines: number) => lines * 25,
    setDoc: (doc: jsPDF) => {
      doc.setFont("linotte");
      doc.setFontSize(16);
      doc.setTextColor("black");
    },
    maxCharsPerLine: 68,
    width: 290,
    characterWidth: 7,
    textOptions: { align: "center" },
  },
  CenterBold: {
    lineHeightFunction: (lines: number) => lines * 25,
    setDoc: (doc: jsPDF) => {
      doc.setFont("linotteBold");
      doc.setFontSize(16);
      doc.setTextColor("black");
    },
    maxCharsPerLine: 68,
    width: 290,
    characterWidth: 7,
    textOptions: { align: "center" },
  },
  Rocker: {
    lineHeightFunction: (lines: number) => lines * 25,
    setDoc: (doc: jsPDF) => {
      doc.setFont("linotteBold");
      doc.setFontSize(16);
      doc.setTextColor("black");
    },
    maxCharsPerLine: 68,
    width: 10,
    characterWidth: 7,
    textOptions: {},
  },
  Stat: {
    lineHeightFunction: (lines: number) => lines * 25,
    setDoc: (doc: jsPDF) => {
      // doc.setFont("Times-Roman");
      doc.setFont("linotteBold");
      doc.setFontSize(18);
      doc.setTextColor("black");
    },
    maxCharsPerLine: 68,
    width: 0,
    characterWidth: 7,
    textOptions: {},
  },
  Title: {
    lineHeightFunction: (lines: number) => lines * 30,
    setDoc: (doc: jsPDF) => {
      doc.setFont("galdeano");
      doc.setFontSize(32);
      doc.setTextColor("black");
    },
    maxCharsPerLine: 30,
    characterWidth: 7,
    width: 290,
    textOptions: { align: "center" },
  },
  Subtitle: {
    lineHeightFunction: (lines: number) => lines * 25,
    setDoc: (doc: jsPDF) => {
      doc.setFont("linotteSuperBold");
      doc.setFontSize(20);
      doc.setTextColor("black");
    },
    maxCharsPerLine: 50,
    characterWidth: 7,
    width: 290,
    textOptions: { align: "center" },
  },
};

type TextSetting = typeof TEXT_SETTINGS.Standard;

export function labelValues(labels: string[], values: number[] | string[]): string[] {
  if (labels.length !== values.length) throw Error("Label size and value size must be equal");
  const texts = [];
  for (let i = 0; i < labels.length; i++) {
    texts.push(`${labels[i]}: ${values[i]}`);
  }
  return texts;
}

export function addEvenlySpacedText(texts: string[], textSetting: TextSetting, doc: jsPDF, height: number) {
  let maxLines = 0;
  const newTexts = [];
  let totalCharacters = 0;
  for (const text of texts) {
    totalCharacters += text.length;
    const [newText, lines] = adjustText(text, textSetting.maxCharsPerLine);
    if (lines > maxLines) maxLines = lines;
    newTexts.push(newText);
  }
  textSetting.setDoc(doc);
  const horizontalSpacing = (documentWidth - totalCharacters * textSetting.characterWidth) / (newTexts.length + 1);
  let width = horizontalSpacing / 2;
  for (const newText of newTexts) {
    addText(newText, textSetting, doc, height, width);
    width += newText.length * textSetting.characterWidth;
    width += horizontalSpacing;
  }
  height += textSetting.lineHeightFunction(maxLines);
  return height;
}

export interface TextAndSettingPair {
  text: string;
  textSetting: TextSetting;
  addedHeight?: number;
}

export function addGroupedText(textAndSettingPairs: TextAndSettingPair[], doc: jsPDF, height: number) {
  let newHeight = height;
  textAndSettingPairs.forEach((textAndSetting) => {
    const [newText, lines] = adjustText(textAndSetting.text, textAndSetting.textSetting.maxCharsPerLine);
    textAndSetting.text = newText;
    const addedHeight = textAndSetting.textSetting.lineHeightFunction(lines);
    textAndSetting.addedHeight = addedHeight;
    newHeight += addedHeight;
  });
  if (newHeight > documentHeight) {
    doc.addPage();
    height = 20;
  }
  textAndSettingPairs.forEach((textAndSetting) => {
    const textSetting = textAndSetting.textSetting;
    textSetting.setDoc(doc);
    doc.text(textAndSetting.text, textSetting.width, height, textSetting.textOptions);
    height += textAndSetting.addedHeight || 0;
  });
  return height;
}

export function addText(text: string, textSetting: TextSetting, doc: jsPDF, height: number, width = textSetting.width) {
  textSetting.setDoc(doc);
  const [newText, lines] = adjustText(text, textSetting.maxCharsPerLine);
  let newHeight = height + textSetting.lineHeightFunction(lines);
  if (newHeight > documentHeight) {
    doc.addPage();
    height = 20;
    newHeight = height + textSetting.lineHeightFunction(lines);
  }
  doc.text(newText, width, height, textSetting.textOptions);
  return newHeight;
}

export async function addImage(url: string, doc: jsPDF, height: number) {
  const res = await fetch(url);
  const blob = await res.blob();
  const dataUrl = URL.createObjectURL(blob);

  const x = (documentWidth - imageWidth) / 2;
  doc.addImage(dataUrl, "jpeg", x, height, imageWidth, imageHeight, "FAST");
  URL.revokeObjectURL(dataUrl);
}

export function newPage(doc: jsPDF): number {
  doc.addPage();
  return 50;
}

/**
 *
 * @param text Text to be modified
 * @param maxCharactersPerLine
 * @returns String representing the given text with new line tags added.
 * And a number representing the number of lines the text will take up.
 */
function adjustText(text: string, maxCharactersPerLine: number): [string, number] {
  const words = text.split(" ");
  let lines = 1;
  let charactersInLine = 0;
  let newWord = "";
  for (const word of words) {
    const wordLength = word.length;
    if (wordLength + charactersInLine > maxCharactersPerLine) {
      newWord += "\n";
      lines += 1;
      charactersInLine = 0;
    } else {
      newWord += " ";
      charactersInLine += 1;
    }
    newWord += word;
    charactersInLine += wordLength;
  }
  return [newWord, lines];
}
